import { logger } from '@teko/log';
import { Address, AddressInput } from '@teko/user';
import { useServices } from 'contexts';
import { t } from 'i18n';
import { useEffect, useState } from 'react';
import { LocationLimit } from './useLocation';
import { useLocationsV2 } from './useLocationV2';

interface UseAddressFormArgs {
  editingAddress?: Partial<Address> | null;
  requireEmail?: boolean;
  platformCode?: string;
  platformId?: number;
  allowAddDefaultAddress?: boolean;
  locationsLimit?: LocationLimit;
}
export const useAddressFormV2 = ({
  editingAddress,
  platformCode,
  platformId,
  allowAddDefaultAddress = false,
  locationsLimit,
}: UseAddressFormArgs) => {
  const { userService } = useServices();

  const [message, setMessage] = useState({
    success: false,
    content: '',
  });
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const [loading, setLoading] = useState(false);
  const {
    provinces,
    wards,
    province,
    ward,
    setProvince,
    setWard,
    setLocation,
  } = useLocationsV2(
    editingAddress?.provinceCode,
    editingAddress?.wardCode,
    locationsLimit,
  );

  const defaultAddress: Partial<Address> = {
    name: '',
    telephone: '',
    address: '',
    email: '',
    isDefault: false,
    ...editingAddress,
    // make sure to remove these 2 fields
    // since server is returning a default 0
    // which would causes errors otherwise
    longitude: editingAddress?.longitude,
    latitude: editingAddress?.latitude,
  };

  useEffect(() => {
    if (!!editingAddress?.latitude && !!editingAddress?.longitude) {
      setPlace({
        long: Number(editingAddress.longitude),
        lat: Number(editingAddress.latitude),
      });
    }
  }, [editingAddress]);

  const [isDefault, setIsDefault] = useState(
    !!defaultAddress.isDefault || allowAddDefaultAddress,
  );

  const submitForm = async (formValues: Address): Promise<Address> => {
    setLoading(true);
    try {
      const input: AddressInput = {
        ...formValues,
        // normalize form value
        name: formValues.name.trim(),
        telephone: formValues.telephone.trim(),
        address: formValues.address.trim(),
        addressNote: formValues.addressNote?.trim() || '',
        // other field
        provinceCode: `${province?.code}`,
        wardCode: `${ward?.code}`,
        platformCode,
        platformId,
        provinceName: province?.title,
        wardName: ward?.title,
        // desktop form use formValue for this boolean
        isDefault: formValues.isDefault || isDefault,
      };
      for (const [key, value] of Object.entries(input)) {
        // allow empty addressNote since it's optional
        if (key === 'addressNote') continue;
        if (value === '' || value === null) {
          delete input[key as keyof AddressInput];
        }
      }

      // UX4BUYER-5419
      // If users want to update email, we have to send null value
      input.email = !!formValues.email ? formValues.email.trim() : null;

      input.note = formValues.note;
      input.longitude = place.long ? `${place.long}` : '';
      input.latitude = place.lat ? `${place.lat}` : '';
      let res;
      if (editingAddress && (editingAddress.id || editingAddress.addressId)) {
        // update address
        await userService.updateAddress(
          editingAddress.id || editingAddress.addressId || '',
          input,
        );
        res = {
          ...editingAddress,
          ...input,
        };
      } else {
        // add new address
        res = await userService.createAddress(input);
      }
      // for backward compatability
      // make sure addressId is set.
      // it's pretty weird that the old code is reading this property
      // while server is returning data without it
      return {
        addressId: res.addressId || res.id,
        ...res,
      };
    } catch (e) {
      logger.error(e);
      setMessage({
        success: false,
        content: t('Có lỗi xảy ra. Vui lòng thử lại sau'),
      });
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return {
    message,
    setMessage,
    isDefault,
    setIsDefault,
    loading,
    submitForm,
    // locations state
    province,
    ward,
    provinces,
    wards,
    setProvince,
    setWard,
    setPlace,
    setLocation,
  };
};
